import { useMemo, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { selectFile } from '@/store/slices/filesSlice';
import { addRecentPhrase } from '@/store/slices/uiSlice';
import { useGetFilesQuery } from '@/store/api/unifiedStorageApi';
import { cn } from '@/lib/utils';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useDraggable } from '@dnd-kit/core';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchInput } from './SearchInput';
import { FileTypeFilter } from './FileTypeFilter';
import { TagsFilter } from './TagsFilter';
import { SortControls } from './SortControls';
import { RecentPhrases } from './RecentPhrases';
import {
  FileText,
  Hash,
  MoreVertical,
  Clock,
  Filter,
  X,
  Folder,
  Star,
} from 'lucide-react';
import type { File, FileType } from '@phrasewright/types';

interface FileListProps {
  className?: string;
}

export function FileList({ className }: FileListProps) {
  const dispatch = useAppDispatch();
  const { selectedFileId } = useAppSelector((state) => state.files);
  const { currentProject } = useAppSelector((state) => state.project);
  const { fileFilters, recentPhrases } = useAppSelector((state) => state.ui);

  // Use RTK Query to fetch files - this will auto-refresh when cache is invalidated
  const { data: files = [], isLoading: loading } = useGetFilesQuery(
    currentProject ? { projectId: currentProject.id } : undefined,
    { skip: !currentProject }
  );

  // Filter and sort files based on current filter state
  const filteredFiles = useMemo(() => {
    // Convert StorageFile to File format for compatibility
    const convertedFiles: File[] = files.map((file) => ({
      id: file.id,
      name: file.name,
      path: (file as any).path || `/${file.type}s/${file.name}`,
      content: file.content,
      createdAt: file.createdAt,
      updatedAt: file.updatedAt,
      revisions: (file as any).revisions || [],
      status: 'active' as const,
      metadata: {
        type: file.type as FileType,
        title:
          file.metadata?.title || file.name.replace(/\.(md|markdown)$/, ''),
        tags: file.tags || [],
        ...file.metadata,
      },
    }));

    let filtered = convertedFiles.filter((file) => {
      // Filter by search query
      if (fileFilters.searchQuery) {
        const query = fileFilters.searchQuery.toLowerCase();
        const matchesTitle = file.name.toLowerCase().includes(query);
        const matchesContent = file.content.toLowerCase().includes(query);
        const matchesTags = file.metadata.tags.some((tag) =>
          tag.toLowerCase().includes(query)
        );
        if (!matchesTitle && !matchesContent && !matchesTags) {
          return false;
        }
      }

      // Filter by file type
      if (
        fileFilters.selectedType !== 'all' &&
        file.metadata.type !== fileFilters.selectedType
      ) {
        return false;
      }

      // Filter by tags
      if (fileFilters.selectedTags.length > 0) {
        const hasMatchingTag = fileFilters.selectedTags.some((tag) =>
          file.metadata.tags.includes(tag)
        );
        if (!hasMatchingTag) {
          return false;
        }
      }

      return true;
    });

    // Sort files
    filtered.sort((a, b) => {
      let comparison = 0;

      switch (fileFilters.sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'type':
          comparison = a.metadata.type.localeCompare(b.metadata.type);
          break;
        case 'created':
          comparison =
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        case 'modified':
        default:
          comparison =
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          break;
      }

      return fileFilters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [files, fileFilters]);

  // Get all unique tags from files for filter component
  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    files.forEach((file) => {
      const tags = file.tags || [];
      tags.forEach((tag) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [files]);

  const handleFileSelect = useCallback(
    (file: File) => {
      dispatch(selectFile(file.id));

      // Track recent phrases for phrase files
      if (file.metadata.type === 'phrase') {
        dispatch(
          addRecentPhrase({
            id: file.id,
            path: file.path || file.name,
            title: file.metadata.title || file.name,
          })
        );
      }
    },
    [dispatch]
  );

  const getFileIcon = (fileType: FileType) => {
    switch (fileType) {
      case 'phrase':
        return <Hash className="h-4 w-4 text-blue-500" />;
      case 'document':
      default:
        return <FileText className="h-4 w-4 text-green-500" />;
    }
  };

  const getFileTypeStyles = (fileType: FileType) => {
    switch (fileType) {
      case 'phrase':
        return 'border-l-2 border-l-blue-500';
      case 'document':
      default:
        return 'border-l-2 border-l-green-500';
    }
  };

  const formatFileDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return 'Today';
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Draggable File Item Component
  const DraggableFileItem = ({ file }: { file: File }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } =
      useDraggable({
        id: `file-${file.id}`,
        data: {
          type: 'file',
          file,
        },
      });

    const style = transform
      ? {
          transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        }
      : undefined;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'flex items-center gap-3 p-2 rounded-md cursor-pointer group',
          'hover:bg-accent transition-colors relative',
          getFileTypeStyles(file.metadata.type),
          selectedFileId === file.id && 'bg-accent',
          isDragging &&
            'opacity-60 z-50 bg-accent border border-border shadow-lg scale-105'
        )}
        onClick={() => handleFileSelect(file)}
      >
        {/* Drag Handle - separate from clickable area */}
        <div
          className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab"
          {...listeners}
          {...attributes}
        >
          <div className="w-2 h-4 flex flex-col justify-center">
            <div className="w-full h-px bg-muted-foreground/50 mb-px"></div>
            <div className="w-full h-px bg-muted-foreground/50 mb-px"></div>
            <div className="w-full h-px bg-muted-foreground/50"></div>
          </div>
        </div>

        <div className="flex-shrink-0">{getFileIcon(file.metadata.type)}</div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-medium truncate">
              {file.metadata.title || file.name}
            </h4>
            <Badge
              variant={
                file.metadata.type === 'phrase' ? 'default' : 'secondary'
              }
              className={cn(
                'text-xs px-1.5 py-0 h-5',
                file.metadata.type === 'phrase'
                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
              )}
            >
              {file.metadata.type}
            </Badge>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{formatFileDate(file.updatedAt)}</span>

            {file.metadata.tags.length > 0 && (
              <>
                <span>•</span>
                <div className="flex gap-1 flex-wrap">
                  {file.metadata.tags.slice(0, 2).map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="text-xs px-1 py-0 h-4"
                    >
                      {tag}
                    </Badge>
                  ))}
                  {file.metadata.tags.length > 2 && (
                    <Badge variant="outline" className="text-xs px-1 py-0 h-4">
                      +{file.metadata.tags.length - 2}
                    </Badge>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => handleFileSelect(file)}>
              Open
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => {
                if (file.metadata.type === 'phrase') {
                  dispatch(
                    addRecentPhrase({
                      id: file.id,
                      path: file.path || file.name,
                      title: file.metadata.title || file.name,
                    })
                  );
                }
              }}
            >
              <Star className="h-3 w-3 mr-2" />
              Add to Recent
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {isDragging && (
          <div className="absolute inset-0 rounded-md bg-primary/10 border-2 border-primary/30 pointer-events-none" />
        )}
      </div>
    );
  };

  const hasActiveFilters =
    fileFilters.searchQuery ||
    fileFilters.selectedType !== 'all' ||
    fileFilters.selectedTags.length > 0;

  if (!currentProject) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center p-6 text-center',
          className
        )}
      >
        <Folder className="h-12 w-12 text-muted-foreground mb-3" />
        <p className="text-sm text-muted-foreground">
          Select a project to view files
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className={cn('flex flex-col h-full', className)}>
        {/* Search and Filters */}
        <div className="p-4 space-y-3 border-b bg-background/50">
          <SearchInput />

          {/* Filter Controls */}
          <div className="flex items-center gap-2 flex-wrap">
            <FileTypeFilter />
            <TagsFilter tags={allTags} />
            <SortControls />

            {hasActiveFilters && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Clear all filters - we'd need to dispatch multiple actions
                    }}
                    className="h-8 px-2"
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Clear filters</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>

        {/* File List */}
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="p-4 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 p-2">
                  <Skeleton className="h-4 w-4 rounded" />
                  <div className="flex-1">
                    <Skeleton className="h-4 w-3/4 mb-1" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredFiles.length > 0 ? (
            <div className="p-2 space-y-1">
              {filteredFiles.map((file) => (
                <DraggableFileItem key={file.id} file={file} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-6 text-center">
              {hasActiveFilters ? (
                <>
                  <Filter className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground mb-2">
                    No files match your filters
                  </p>
                  <Button variant="ghost" size="sm" onClick={() => {}}>
                    Clear filters
                  </Button>
                </>
              ) : (
                <>
                  <FileText className="h-12 w-12 text-muted-foreground mb-3" />
                  <p className="text-sm text-muted-foreground">
                    No files in this project
                  </p>
                </>
              )}
            </div>
          )}
        </ScrollArea>

        {/* Recent Phrases */}
        {recentPhrases.length > 0 && (
          <>
            <div className="border-t" />
            <RecentPhrases className="shrink-0" />
          </>
        )}
      </div>
    </TooltipProvider>
  );
}
